import z from "zod";
import ShoppingCategoryService from "../../../../services/ShoppingCategoryService";
import { mapErrorsToFields } from "../../../../utils";

const api = new ShoppingCategoryService();

export type State = {
  message: string | null;
  errors?: {
    name?: string[];
    general?: string[];
  };
  success: boolean;
  defaultValues: {
    name?: string;
  };
};

const FormSchema = z.object({
  name: z
    .string()
    .min(1, { message: "Name is required." })
    .max(200, { message: "Name cannot exceed 200 characters." }),
});

function apiMessage(error: any, fallback: string) {
  return (
    error?.response?.data?.description ??
    error?.response?.data?.message ??
    error?.response?.data?.Message ??
    fallback
  );
}

export async function createShoppingCategory(
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to create shopping category.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.createShoppingCategory(validatedFields.data);
    if (result.status === 201) {
      return {
        errors: {},
        message: "Shopping category created successfully.",
        defaultValues,
        success: true,
      };
    }
  } catch (error) {
    const message = apiMessage(error, "Failed to create shopping category.");
    return {
      errors: { general: [message] },
      message,
      defaultValues,
      success: false,
    };
  }
  return {
    errors: { general: ["Failed to create shopping category."] },
    message: "Failed to create shopping category.",
    defaultValues,
    success: false,
  };
}

export async function updateShoppingCategory(
  id: string,
  prevState: State,
  formData: FormData
): Promise<State> {
  const defaultValues = {
    name: formData.get("name")?.toString() || "",
  };
  try {
    const validatedFields = FormSchema.safeParse(defaultValues);
    if (!validatedFields.success) {
      return {
        errors: mapErrorsToFields(validatedFields.error),
        message: "Failed to update shopping category.",
        defaultValues,
        success: false,
      };
    }
    const result = await api.updateShoppingCategory({
      id: parseInt(id),
      ...validatedFields.data,
    });
    if (result.status === 204) {
      return {
        errors: {},
        message: "Shopping category updated successfully.",
        defaultValues,
        success: true,
      };
    }
  } catch (error) {
    const message = apiMessage(error, "Failed to update shopping category.");
    return {
      errors: { general: [message] },
      message,
      defaultValues,
      success: false,
    };
  }
  return {
    errors: { general: ["Failed to update shopping category."] },
    message: "Failed to update shopping category.",
    defaultValues,
    success: false,
  };
}

export async function deleteShoppingCategory(
  id: string
): Promise<{ success: boolean; message?: string }> {
  try {
    const result = await api.deleteShoppingCategory(parseInt(id));
    return { success: result.status === 204 };
  } catch (error) {
    return {
      success: false,
      message: apiMessage(error, "Failed to delete shopping category."),
    };
  }
}
